import { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function listFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.closest('[aria-hidden="true"]') == null
  );
}

/**
 * Slot for JSX passed into the panel. Aliased to `any` because TS 4.9 + `@types/react` 18.3 (`export =` + `typesVersions`)
 * does not expose `React.ReactNode` on module `"react"` in ESM `.tsx` files (TS2694 / TS2709 on `ReactNode`).
 */
export type SlideOverPanelSlot = any;

export interface SlideOverPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Tailwind max-width class, e.g. "max-w-md" or "max-w-lg". Defaults to "max-w-md". */
  widthClass?: string;
  /** CSS class applied to the panel root for keyframe animation targeting. */
  animationClass?: string;
  children: SlideOverPanelSlot;
  footer?: SlideOverPanelSlot;
}

export default function SlideOverPanel({
  isOpen,
  onClose,
  title,
  widthClass = 'max-w-md',
  animationClass = 'slide-over-panel',
  children,
  footer,
}: SlideOverPanelProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const prevFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    prevFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const focusPanel = () => {
      const root = panelRef.current;
      if (!root) return;
      const focusables = listFocusable(root);
      const target = focusables[0] ?? root;
      target.focus();
    };

    const id = window.requestAnimationFrame(() => focusPanel());

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const root = panelRef.current;
      if (!root) return;
      const focusables = listFocusable(root);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener('keydown', onKeyDown);
      const prev = prevFocusRef.current;
      if (prev && document.body.contains(prev)) {
        prev.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40 transition-opacity" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`fixed top-0 right-0 h-full w-full ${widthClass} bg-white shadow-xl z-50 flex flex-col overflow-hidden outline-none ${animationClass}`}
      >
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 id={titleId} className="text-base font-medium text-gray-900">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-5 space-y-4">{children}</div>
        {footer ? (
          <div className="flex-shrink-0 px-5 pt-4 pb-8 border-t border-gray-200 bg-white flex justify-end gap-2">
            {footer}
          </div>
        ) : null}
      </div>
    </>
  );
}
